// The grouped menu on the rail, adapted from design/isomorphic/src/layouts/sidebar-menu.tsx
// (Isomorphic's helium-sidebar-menu): the active item is a lit pill with an accent icon.
// Groups marked `collapsed` (Platform) fold until opened or until one of their pages is open;
// items the user's permissions do not allow are left out (D51). On the folded rail (`compact`)
// items are icons with their label as a tooltip, and group headings become dividers.
import { CaretDown } from "@phosphor-icons/react";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { Tooltip } from "rizzui";

import { usePermissions } from "../auth/permissions";
import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { useAttention } from "./attention";
import { isActive, NAV, type NavItem, SECTIONS, type SectionKey } from "./nav";

function useBadges(): Partial<Record<SectionKey, number>> {
  const { questions, pendingDevices } = useAttention();
  return { runs: questions.length, devices: pendingDevices.length };
}

function useAllowed(): (item: NavItem) => boolean {
  const { superAdmin, can } = usePermissions();
  return ({ needs }) => needs === undefined || (needs === "super-admin" ? superAdmin : typeof needs === "string" ? can(needs) : needs.some(can));
}

export function SidebarMenu({ compact = false, onNavigate }: { compact?: boolean; onNavigate?: () => void }) {
  const t = useT().shell;
  const { pathname } = useLocation();
  const allowed = useAllowed();
  const badges = useBadges();
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const groups = NAV.map((g) => ({ ...g, items: g.items.filter(allowed) })).filter((g) => g.items.length > 0);
  const Heading = compact ? GroupDivider : GroupHeading;
  return (
    <nav className="mt-2 pb-4" aria-label={t.sections}>
      {groups.map((group, gi) => {
        const current = group.items.some((i) => isActive(pathname, i.path));
        const open = !group.collapsed || current || opened[group.key] === true;
        const hidden = open ? 0 : group.items.reduce((n, i) => n + (badges[i.key] ?? 0), 0);
        return (
          <div key={group.key}>
            <Heading
              name={t.groups[group.key]}
              first={gi === 0}
              open={open}
              badge={hidden}
              onToggle={group.collapsed && !current ? () => setOpened((o) => ({ ...o, [group.key]: !open })) : undefined}
            />
            {open
              ? group.items.map((item) => (
                  <MenuLink key={item.key} item={item} pathname={pathname} badge={badges[item.key] ?? 0} compact={compact} onNavigate={onNavigate} />
                ))
              : null}
          </div>
        );
      })}
    </nav>
  );
}

interface HeadingProps {
  name: string;
  first: boolean;
  open: boolean;
  /** What the folded group's items are waiting on. */
  badge: number;
  onToggle?: () => void;
}

const headingClass = "mb-2 flex w-full items-center justify-between truncate px-6 text-[11px] font-medium uppercase tracking-[0.16em] text-rail-muted";

function GroupHeading({ name, first, open, badge, onToggle }: HeadingProps) {
  if (!onToggle) return <div className={cn(headingClass, !first && "mt-6")}>{name}</div>;
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className={cn(headingClass, "hover:text-rail-foreground", !first && "mt-6")}>
      <span>{name}</span>
      <span className="flex items-center gap-2">
        {badge > 0 ? <Badge count={badge} /> : null}
        <Caret open={open} />
      </span>
    </button>
  );
}

function GroupDivider({ name, first, open, badge, onToggle }: HeadingProps) {
  return (
    <>
      {first ? null : <div className="mx-5 my-3 border-t border-rail-foreground/15" aria-hidden />}
      {onToggle ? (
        <Tooltip content={name} placement="right">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={name}
            className="relative mx-auto mb-1 flex h-7 w-10 items-center justify-center rounded-lg text-rail-muted hover:bg-gray-1000/[0.05] hover:text-rail-foreground"
          >
            <Caret open={open} />
            {badge > 0 ? <span className="absolute end-1 top-1 h-2 w-2 rounded-full bg-orange" aria-hidden /> : null}
          </button>
        </Tooltip>
      ) : null}
    </>
  );
}

function Caret({ open }: { open: boolean }) {
  return <CaretDown weight="bold" className={cn("h-3 w-3 transition-transform", open && "rotate-180")} aria-hidden />;
}

function Badge({ count, corner = false }: { count: number; corner?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-orange font-semibold tracking-normal text-gray-1000 dark:text-gray-0",
        corner ? "absolute end-0.5 top-0.5 h-4 min-w-4 px-1 text-[10px]" : "h-5 min-w-5 px-1.5 text-[11px]",
      )}
      aria-hidden
    >
      {count}
    </span>
  );
}

interface MenuLinkProps {
  item: NavItem;
  pathname: string;
  badge: number;
  compact: boolean;
  onNavigate?: () => void;
}

function MenuLink({ item: { key, path, Icon }, pathname, badge, compact, onNavigate }: MenuLinkProps) {
  useT();
  const active = isActive(pathname, path);
  const title = SECTIONS[key].title;
  const link = (
    <Link
      to={path}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative my-0.5 flex items-center rounded-lg py-2.5 font-medium transition-colors duration-200",
        // Folded, the menu keeps a scroll-bar gutter on both sides (rail-scrollbar), so less margin.
        compact ? "mx-1.5 justify-center" : "mx-3 justify-between px-3",
        active ? "bg-gray-1000/[0.09] text-gray-1000" : "text-rail-foreground hover:bg-gray-1000/[0.05] hover:text-gray-1000",
      )}
    >
      <span className="flex items-center truncate">
        <Icon
          weight="duotone"
          className={cn("h-5 w-5 shrink-0", !compact && "me-2.5", active ? "text-rail-accent" : "text-rail-foreground/70 group-hover:text-gray-1000")}
        />
        <span className={compact ? "sr-only" : "truncate"}>{title}</span>
      </span>
      {badge > 0 ? <Badge count={badge} corner={compact} /> : null}
    </Link>
  );
  return compact ? (
    <Tooltip content={title} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}
